import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";
import { AlertTriangle } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog.tsx";
import { Spinner } from "@/components/ui/spinner.tsx";

export interface ConfirmOptions {
  title: string;
  description?: ReactNode;
  confirmText?: string;
  cancelText?: string;
  destructive?: boolean;
  onConfirm?: () => Promise<unknown> | unknown;
}

type ConfirmFn = (opts: ConfirmOptions) => Promise<boolean>;

const Ctx = createContext<ConfirmFn | null>(null);

interface Pending extends ConfirmOptions {
  resolve: (v: boolean) => void;
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<Pending | null>(null);
  const [busy, setBusy] = useState(false);
  const resolved = useRef(false);

  const confirm = useCallback<ConfirmFn>(
    (opts) =>
      new Promise<boolean>((resolve) => {
        resolved.current = false;
        setPending({ ...opts, resolve });
      }),
    [],
  );

  const finish = (v: boolean) => {
    if (!pending || resolved.current) return;
    resolved.current = true;
    pending.resolve(v);
    setPending(null);
    setBusy(false);
  };

  const onConfirm = async () => {
    if (!pending) return;
    if (pending.onConfirm) {
      setBusy(true);
      try {
        await pending.onConfirm();
      } catch {
        setBusy(false);
        return;
      }
    }
    finish(true);
  };

  const value = useMemo(() => confirm, [confirm]);

  return (
    <Ctx.Provider value={value}>
      {children}
      <AlertDialog open={!!pending} onOpenChange={(o) => !o && !busy && finish(false)}>
        {pending && (
          <AlertDialogContent onEscapeKeyDown={(e) => busy && e.preventDefault()}>
            <AlertDialogHeader>
              <div className="flex items-start gap-3">
                {pending.destructive && (
                  <span className="grid size-9 shrink-0 place-items-center rounded-full bg-destructive/10 text-destructive" aria-hidden="true">
                    <AlertTriangle className="size-4" />
                  </span>
                )}
                <div className="space-y-1.5">
                  <AlertDialogTitle>{pending.title}</AlertDialogTitle>
                  {pending.description && <AlertDialogDescription>{pending.description}</AlertDialogDescription>}
                </div>
              </div>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={busy} onClick={() => finish(false)}>
                {pending.cancelText ?? "Cancel"}
              </AlertDialogCancel>
              <AlertDialogAction variant={pending.destructive ? "destructive" : "default"} disabled={busy} onClick={() => void onConfirm()} autoFocus>
                {busy && <Spinner size={14} />}
                {pending.confirmText ?? (pending.destructive ? "Delete" : "Confirm")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        )}
      </AlertDialog>
    </Ctx.Provider>
  );
}

export function useConfirm(): ConfirmFn {
  const v = useContext(Ctx);
  if (!v) throw new Error("useConfirm must be used inside <ConfirmProvider>");
  return v;
}

export interface ConfirmDialogProps extends ConfirmOptions {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => Promise<unknown> | unknown;
  loading?: boolean;
}

export function ConfirmDialog({ open, onOpenChange, title, description, confirmText, cancelText, destructive, onConfirm, loading }: ConfirmDialogProps) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          {description && <AlertDialogDescription>{description}</AlertDialogDescription>}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={loading}>{cancelText ?? "Cancel"}</AlertDialogCancel>
          <AlertDialogAction variant={destructive ? "destructive" : "default"} disabled={loading} onClick={() => void onConfirm()}>
            {loading && <Spinner size={14} />}
            {confirmText ?? (destructive ? "Delete" : "Confirm")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default ConfirmDialog;
